import { Client, createClient } from '@hey-api/client-fetch';
import { useQueryClient } from '@tanstack/solid-query';
import {
  Component,
  createContext,
  JSX,
  useContext,
  createMemo,
  Show,
} from 'solid-js';
import { CommentApiService } from './api-services/comment-api-service';
import {
  CommentConfiguration,
  DEFAULT_COMMENT_CONFIG,
} from './constants/comment-config';

interface CommentApiServices {
  readonly commentApi: CommentApiService;
}

const CommentApiServiceContext = createContext<CommentApiServices | undefined>(
  undefined
);

// Helper function to validate context is properly initialized
const validateContext = (
  context: CommentApiServices | undefined
): context is CommentApiServices => {
  if (!context) {
    return false;
  }

  // Validate that the context has the expected structure
  if (!context.commentApi || typeof context.commentApi !== 'object') {
    console.error(
      'CommentApiServiceContext: Invalid context structure - missing commentApi'
    );
    return false;
  }

  return true;
};

export interface CommentApiServiceProviderProps {
  readonly children?: JSX.Element;
  readonly accessToken: string;
  readonly configuration?: Partial<CommentConfiguration>;
}

export const CommentApiServiceProvider: Component<
  CommentApiServiceProviderProps
> = (props: CommentApiServiceProviderProps) => {
  const queryClient = useQueryClient();

  // Use createMemo to access reactive props
  const services = createMemo(() => {
    try {
      const accessToken = props.accessToken;
      if (!accessToken || accessToken.length === 0) {
        throw new Error('Access token is empty or missing');
      }

      const config = { ...DEFAULT_COMMENT_CONFIG, ...props.configuration };
      const client = configureClient(accessToken, config.apiBaseUrl);
      const commentApi = new CommentApiService(client, queryClient);

      return {
        commentApi,
      };
    } catch (error) {
      console.error('Failed to setup comment services:', error);
      return null;
    }
  });

  return (
    <Show
      when={services() !== null}
      fallback={
        <div class="error-message">Failed to setup comment services</div>
      }
    >
      {/* eslint-disable-next-line solid/reactivity */}
      <CommentApiServiceContext.Provider value={services()!}>
        {props.children}
      </CommentApiServiceContext.Provider>
    </Show>
  );
};

export const useCommentApiServices = (): CommentApiServices => {
  const context = useContext(CommentApiServiceContext);

  if (!validateContext(context)) {
    console.error(
      'CommentApiServiceContext is invalid. This can happen if:',
      '\n1. Component is not wrapped with CommentApiServiceProvider',
      '\n2. HMR (Hot Module Replacement) caused context to become stale',
      '\n3. Provider failed to initialize due to missing props',
      '\n4. Context was corrupted during development',
      '\nContext reference:',
      CommentApiServiceContext,
      '\nContext value:',
      context
    );
    throw new Error(
      'useCommentApiServices must be used within a CommentApiServiceProvider. ' +
        'Ensure your component is wrapped with CommentContainer or CommentProvider.'
    );
  }

  return context;
};

export const useCommentApi = (): CommentApiService => {
  const services = useCommentApiServices();
  return services.commentApi;
};

const configureClient = (accessToken: string, baseUrl: string): Client => {
  const client = createClient({ baseUrl, global: false });

  client.interceptors.request.use((request) => {
    request.headers.set('Authorization', `Bearer ${accessToken}`);
    request.headers.set('Content-Type', 'application/json');
    return request;
  });

  return client;
};
